<template>
  <!-- Loading State -->
  <LoadingSpinner
    v-if="loading"
    size="lg"
    color="primary"
    :text="t('app.views.tables.loading')"
  />

  <!-- Error State -->
  <div 
    v-else-if="error" 
    class="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4 mb-4 rounded-r-lg"
  >
    <div class="flex">
      <div class="flex-shrink-0">
        <XCircleIcon
          class="h-5 w-5 text-red-400"
          aria-hidden="true"
        />
      </div>
      <div class="ml-3">
        <p class="text-sm text-red-700 dark:text-red-300">
          {{ error }}
        </p>
      </div>
    </div>
  </div>

  <!-- Empty State -->
  <EmptyState
    v-else-if="isEmpty"
    icon="table"
    :title="t('app.views.tables.no_tables')"
    :description="t('app.views.tables.no_tables_description')"
    variant="card"
    size="md"
  >
    <template #actions>
      <BaseButton
        v-if="canManageTables"
        variant="primary"
        @click="$emit('add-table')"
      >
        <template #icon>
          <PlusIcon
            class="w-4 h-4"
          />
        </template>
        {{ t('app.views.tables.add_table') }}
      </BaseButton>
    </template>
  </EmptyState>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { XCircleIcon, PlusIcon } from '@heroicons/vue/24/outline';
import BaseButton from '@/components/ui/BaseButton.vue';
import LoadingSpinner from '@/components/base/LoadingSpinner.vue';
import EmptyState from '@/components/base/EmptyState.vue';

interface Props {
  loading?: boolean;
  error?: string;
  isEmpty?: boolean;
  canManageTables?: boolean;
}

withDefaults(defineProps<Props>(), {
  loading: false,
  error: '',
  isEmpty: false,
  canManageTables: false
});

defineEmits<{
  'add-table': [];
}>();

const { t } = useI18n();
</script>
